// <Modal> — scrim + centered card per DESIGN.md §7.5.
//
// role="dialog" aria-modal="true", focus trap, Escape to close for
// non-blocking variants. Max-width 480px (confirm) / 720px (detail).
//
// Props:
//   open: boolean
//   onClose: () => void (called on Escape or scrim click unless `blocking`)
//   title: string (used as aria-labelledby)
//   description?: string
//   variant: 'confirm' | 'detail'
//   blocking?: boolean — disables Escape + scrim close
//   footer?: ReactNode — usually <Button>s
//
// Focus behavior:
//   - On open, focus moves to the first focusable element inside the card.
//   - Tab/Shift+Tab cycle is trapped inside the card.
//   - On close, focus returns to the element that was focused before open.

import type { ReactNode } from "react";
import { useCallback, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import "./Modal.css";

export type ModalVariant = "confirm" | "detail";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  variant?: ModalVariant;
  blocking?: boolean;
  footer?: ReactNode;
  children?: ReactNode;
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll<HTMLElement>(selector));
}

export function Modal(props: ModalProps): JSX.Element | null {
  const {
    open,
    onClose,
    title,
    description,
    variant = "confirm",
    blocking = false,
    footer,
    children,
  } = props;

  const cardRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  // Focus trap + initial focus + restore.
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    // Defer to next tick so the card is in the DOM.
    const t = window.setTimeout(() => {
      const focusables = cardRef.current ? getFocusable(cardRef.current) : [];
      if (focusables.length > 0) {
        focusables[0]?.focus();
      } else {
        cardRef.current?.focus();
      }
    }, 0);
    return () => {
      window.clearTimeout(t);
      previouslyFocusedRef.current?.focus();
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape" && !blocking) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      if (!cardRef.current) return;
      const focusables = getFocusable(cardRef.current);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [blocking, onClose],
  );

  if (!open) return null;

  const content = (
    <div
      className="rb-modal-scrim"
      onMouseDown={(e) => {
        // Close only if the user clicks the scrim itself, not a bubbling
        // event from inside the card.
        if (!blocking && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        className={`rb-modal-card rb-modal-card--${variant}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <header className="rb-modal-card__header">
          <h2 id={titleId} className="rb-modal-card__title">
            {title}
          </h2>
          {description && (
            <p id={descId} className="rb-modal-card__description">
              {description}
            </p>
          )}
        </header>
        {children && <div className="rb-modal-card__body">{children}</div>}
        {footer && <footer className="rb-modal-card__footer">{footer}</footer>}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
